import { useApolloClient, useQuery } from '@apollo/client';
import {
  type AllTableCriteria,
  type CriteriaChange,
  CriteriaDimension,
  SortDirection,
} from '@openx/types/tableCriteria.ts';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria.ts';
import { useCallback, useMemo } from 'react';
import { TABLE_PAGE_SIZE } from '../../../../config.ts';
import {
  GetBidderCreativesListDocument,
  type GetBidderCreativesListQuery,
  type GetBidderCreativesListQueryVariables,
} from '../../../../graphql/query/creatives/GetBidderCreativesList.generated.tsx';
import { findLastPopulatedPage } from '../../../../utils/findLastPopulatedPage.ts';
import { useLastPopulatedPageRedirect } from '../../../../utils/useLastPopulatedPageRedirect.ts';
import { type DefaultTableCriteria, useListCriteria } from '../../../../utils/useListCriteria.ts';
import type { CreativeRow, CreativesListFilters } from '../types.ts';

type CreativeFilterKey = `${keyof CreativeRow}${string}`;

const criteriaToApiKeyMap = {
  phrase: 'name__contains',
  status: 'status',
  ad_format: 'ad_format',
} satisfies Record<keyof CreativesListFilters, CreativeFilterKey>;

export const defaultCriteria: DefaultTableCriteria<'creatives'> = {
  [CriteriaDimension.PAGINATION]: {
    pageNumber: 1,
    pageSize: TABLE_PAGE_SIZE,
    totalCount: 0,
    hasMore: false,
  },
  [CriteriaDimension.SORT]: {
    column: 'created_date',
    direction: SortDirection.DESC,
    sortableColumns: ['created_date', 'status', 'name', 'ad_format'] as const,
  },
  [CriteriaDimension.FILTERS]: {
    phrase: '',
  },
};

export const useCreativesListFetch = () => {
  const apolloClient = useApolloClient();
  const { criteria, setCriteria } = useListCriteria('creatives', defaultCriteria);

  const { pageNumber, pageSize } = criteria[CriteriaDimension.PAGINATION] ?? defaultCriteria.pagination;

  // Fetch 2 pages at once so the next-page navigation is instant (Apollo cache hit).
  const prefetchLimit = pageSize * 2;
  const prefetchOffset = Math.floor((pageNumber - 1) / 2) * prefetchLimit;
  const currentPageOffset = (pageNumber - 1) % 2 === 1 ? pageSize : 0;

  const filter = useMemo(() => {
    const filters: { key: string; value: string }[] = [];

    for (const [criteriaKey, apiKey] of Object.entries(criteriaToApiKeyMap)) {
      const value = criteria[CriteriaDimension.FILTERS]?.[criteriaKey as keyof CreativesListFilters];

      if (value) {
        filters.push({
          key: apiKey,
          value,
        });
      }
    }

    const sortColumn = criteria[CriteriaDimension.SORT]?.column;

    if (sortColumn) {
      const direction = criteria[CriteriaDimension.SORT]?.direction;

      filters.push({
        key: 'sort',
        value: direction === SortDirection.DESC ? `-${sortColumn}` : sortColumn,
      });
    }

    return filters.length > 0 ? filters : undefined;
  }, [criteria]);

  const { data, loading, error } = useQuery(GetBidderCreativesListDocument, {
    variables: {
      limit: prefetchLimit,
      offset: prefetchOffset,
      filter,
    },
  });

  const creatives = useMemo(
    () => (data?.bidderCreatives?.objects ?? []).slice(currentPageOffset, currentPageOffset + pageSize),
    [data?.bidderCreatives?.objects, currentPageOffset, pageSize]
  );

  const resolveLastCreativePage = useCallback(
    (outOfRangePage: number, abortSignal?: AbortSignal) =>
      findLastPopulatedPage({
        requestedPage: outOfRangePage,
        abortSignal,

        isPagePopulated: async (candidatePage, signal) => {
          const probeOffset = (candidatePage - 1) * pageSize;

          const { data: probeData } = await apolloClient.query<
            GetBidderCreativesListQuery,
            GetBidderCreativesListQueryVariables
          >({
            query: GetBidderCreativesListDocument,
            variables: {
              limit: 1,
              offset: probeOffset,
              filter,
            },
            fetchPolicy: 'no-cache',
            context: {
              fetchOptions: {
                signal,
              },
            },
          });

          return (probeData.bidderCreatives?.objects.length ?? 0) > 0;
        },
      }),
    [apolloClient, filter, pageSize]
  );

  const redirectToCreativePage = useCallback(
    (correctedPage: number, outOfRangePage: number) => {
      setCriteria(previousCriteria => {
        const previousPagination = previousCriteria[CriteriaDimension.PAGINATION] ?? defaultCriteria.pagination;

        if (previousPagination.pageNumber !== outOfRangePage) {
          return previousCriteria;
        }

        return {
          ...previousCriteria,
          [CriteriaDimension.PAGINATION]: {
            ...previousPagination,
            pageNumber: correctedPage,
          },
        };
      });
    },
    [setCriteria]
  );

  const redirectToFirstCreativePage = useCallback(() => {
    redirectToCreativePage(1, pageNumber);
  }, [pageNumber, redirectToCreativePage]);

  useLastPopulatedPageRedirect({
    enabled: data?.bidderCreatives != null,
    requestedPage: pageNumber,
    loading,
    objectsLength: creatives.length,
    hasMore: data?.bidderCreatives?.has_more,
    resolveLastPopulatedPage: resolveLastCreativePage,
    onRedirect: redirectToCreativePage,
    onFailure: redirectToFirstCreativePage,
  });

  const criteriaWithTotal = useMemo(
    () => ({
      ...criteria,
      [CriteriaDimension.PAGINATION]: {
        pageNumber,
        pageSize,
        totalCount: prefetchOffset + Math.min(data?.bidderCreatives?.objects?.length ?? 0, prefetchLimit),
        hasMore: data?.bidderCreatives?.has_more ?? false,
      },
    }),
    [
      criteria,
      data?.bidderCreatives?.has_more,
      data?.bidderCreatives?.objects?.length,
      pageNumber,
      pageSize,
      prefetchLimit,
      prefetchOffset,
    ]
  );

  const handleCriteriaChange = useCallback(
    (newCriteria: CriteriaChange<CriteriaDimension, Record<string, unknown>>) => {
      setCriteria(previousCriteria => {
        const updated = applyCriteriaChange(previousCriteria, newCriteria) as AllTableCriteria<CreativesListFilters>;

        if (newCriteria.dimension === CriteriaDimension.FILTERS) {
          return {
            ...updated,
            [CriteriaDimension.PAGINATION]: {
              ...(updated[CriteriaDimension.PAGINATION] ?? defaultCriteria[CriteriaDimension.PAGINATION]),
              pageNumber: 1,
            },
          };
        }

        return updated;
      });
    },
    [setCriteria]
  );

  return {
    creatives,
    error,
    loading,
    criteria: criteriaWithTotal,
    handleCriteriaChange,
  };
};
